import React, { useEffect, useState } from 'react';
import { Receipt, Loader2, RefreshCcw, CheckCircle2, XCircle, Clock, Search, ExternalLink } from 'lucide-react';
import { adminApi } from '../lib/api';

const formatFCFA = (n) => new Intl.NumberFormat('fr-FR').format(n || 0);
const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
};

const statusMeta = {
  paid: { label: 'Payée', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  approved: { label: 'Approuvée', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-700', icon: Clock },
  failed: { label: 'Échec', cls: 'bg-red-100 text-red-700', icon: XCircle },
  declined: { label: 'Refusée', cls: 'bg-red-100 text-red-700', icon: XCircle },
  unknown: { label: 'Inconnu', cls: 'bg-slate-100 text-slate-700', icon: Clock },
};

const Transactions = () => {
  const [data, setData] = useState({ transactions: [], stats: { approved: 0, pending: 0, declined: 0, total_amount_ttc: 0 } });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');

  const load = async () => {
    setRefreshing(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      const d = await adminApi.listTransactions(params);
      setData(d);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); setRefreshing(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const verifyOne = async (ref) => {
    try {
      await adminApi.verifyPayment(ref);
      await load();
    } catch (e) { alert('Erreur vérification : ' + (e?.response?.data?.detail || e.message)); }
  };

  const filtered = data.transactions.filter((t) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (t.reference || '').toLowerCase().includes(s)
      || (t.customer_name || '').toLowerCase().includes(s)
      || (t.customer_email || '').toLowerCase().includes(s)
      || (t.fedapay_transaction_id || '').toString().includes(s);
  });

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#F4A340]" /></div>;

  return (
    <div className="space-y-4" data-testid="transactions-list">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Transactions FedaPay</h3>
          <p className="text-sm text-slate-500">Historique de tous les paiements en ligne (réservations + commandes)</p>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          data-testid="transactions-refresh-btn"
          className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-slate-200 hover:border-[#F4A340] rounded-full text-sm font-semibold transition"
        >
          <RefreshCcw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Actualiser
        </button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-2xl p-4 border border-slate-200">
          <div className="text-xs text-slate-500">Approuvées</div>
          <div className="text-2xl font-bold text-emerald-600 mt-1" data-testid="stat-approved">{data.stats.approved}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-200">
          <div className="text-xs text-slate-500">En attente</div>
          <div className="text-2xl font-bold text-amber-600 mt-1" data-testid="stat-pending">{data.stats.pending}</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-slate-200">
          <div className="text-xs text-slate-500">Refusées</div>
          <div className="text-2xl font-bold text-red-600 mt-1" data-testid="stat-declined">{data.stats.declined}</div>
        </div>
        <div className="bg-gradient-to-br from-[#F4A340]/10 to-[#E89534]/10 rounded-2xl p-4 border border-[#F4A340]/30">
          <div className="text-xs text-slate-500">Total encaissé TTC</div>
          <div className="text-xl font-bold text-[#E89534] mt-1">{formatFCFA(data.stats.total_amount_ttc)} F</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        {['all', 'paid', 'pending', 'failed'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${filter === f ? 'bg-slate-900 text-white' : 'bg-white border-2 border-slate-200 hover:border-slate-300'}`}
          >
            {f === 'all' ? 'Toutes' : f === 'paid' ? 'Approuvées' : f === 'pending' ? 'En attente' : 'Refusées'}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            placeholder="Rechercher (réf, email, id)..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 pr-4 py-2 rounded-full border-2 border-slate-200 focus:border-[#F4A340] outline-none text-sm bg-white w-64"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-slate-500 uppercase text-[10px] tracking-widest">
                <th className="px-4 py-3">Référence</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3 text-right">HT / TVA / TTC</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-12 text-center text-slate-400">
                  <Receipt className="w-10 h-10 mx-auto mb-2 opacity-40" />
                  Aucune transaction pour le moment
                </td></tr>
              ) : filtered.map((t) => {
                const meta = statusMeta[t.payment_status] || statusMeta.unknown;
                const Icon = meta.icon;
                return (
                  <tr key={`${t.kind}-${t.reference}`} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono font-bold text-slate-900">{t.reference}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${t.kind === 'reservation' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {t.kind === 'reservation' ? 'Réservation' : 'Commande'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-800">{t.customer_name || '—'}</div>
                      <div className="text-xs text-slate-400">{t.customer_email || '—'}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-mono">
                      <div className="text-slate-500 text-xs">{formatFCFA(t.amount_ht)} + {formatFCFA(t.vat)}</div>
                      <div className="font-bold text-slate-900">{formatFCFA(t.amount_ttc)} F</div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-bold ${meta.cls}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                      {t.fedapay_status && t.fedapay_status !== t.payment_status && (
                        <div className="text-[10px] text-slate-400 mt-1">FedaPay : {t.fedapay_status}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">{formatDate(t.created_at)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {t.payment_status !== 'paid' && (
                          <button
                            onClick={() => verifyOne(t.reference)}
                            className="text-xs text-[#E89534] hover:underline font-semibold"
                            data-testid={`verify-${t.reference}`}
                          >
                            Vérifier
                          </button>
                        )}
                        <a
                          href={`/suivi/${t.reference}`}
                          target="_blank" rel="noreferrer"
                          className="text-slate-400 hover:text-slate-700"
                          title="Voir le suivi"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Transactions;
